import store from '@/store'
import { fetchOrderConversationHistory } from '@/services/orderConversations'

/**
 * Mutation types of the store
 */
const mutationTypes = {
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_ALERTS_AS_SEEN: 'SET_ALERTS_AS_SEEN',
  SET_NOTIFICATION_DEFAULTS: 'SET_NOTIFICATION_DEFAULTS',
  SET_ACTIONABLE_SUPPORT_MESSAGES: 'SET_ACTIONABLE_SUPPORT_MESSAGES'
}

/**
 * Initial state
 */

const defaultState = {
  notifications: [],
  hasSeenAlerts: false,
  actionableSupportMessages: { orders: 0, total: 0 }
}

const state = {
  ...defaultState
}

/**
 * Getters
 */
const getters = {
  alertData: (state, localGetters) => {
    const data = {}

    if (localGetters.hasActionableOrders) {
      const {
        awaitingDespatchCount = 0,
        awaitingDeliveryCount = 0,
        draftCount = 0
      } = store.state.purchaseOrders?.counters || {}

      const totalQty =
        awaitingDespatchCount + awaitingDeliveryCount + draftCount

      data.purchaseOrders = {
        deliveredTabQty: awaitingDeliveryCount,
        inTransitTabQty: awaitingDespatchCount,
        newTabQty: draftCount,
        totalQty
      }
    }

    if (localGetters.hasActionableOrderQueries) {
      data.orderQueries = {
        outstandingQty:
          store.state.orderQueries?.counters?.outstandingCount || 0
      }
    }

    if (localGetters.hasActionableCreditReturns) {
      data.creditReturns = {
        outstandingQty:
          store.state.creditReturns?.counters?.outstandingCount || 0
      }
    }

    if (localGetters.hasActionableConsents) {
      data.actionableConsents = {
        totalQty: store.getters['retailerInvites/actionableConsents'].length
      }
    }

    if (localGetters.hasActionableSupportMessages) {
      data.actionableSupportMessages = state.actionableSupportMessages
    }

    return data
  },

  displayAlerts: (state, getters) => {
    return (
      !state.hasSeenAlerts &&
      (getters.hasActionableOrders ||
        getters.hasActionableOrderQueries ||
        getters.hasActionableCreditReturns ||
        getters.hasActionableConsents ||
        !!getters.hasActionableSupportMessages)
    )
  },

  hasActionableConsents: () => {
    return store.getters['retailerInvites/actionableConsents'].length > 0
  },

  hasActionableOrders: () => {
    return (
      (store.state.purchaseOrders?.counters?.awaitingDespatchCount || 0) > 0 ||
      (store.state.purchaseOrders?.counters?.awaitingDeliveryCount || 0) > 0 ||
      (store.state.purchaseOrders?.counters?.draftCount || 0) > 0
    )
  },

  hasActionableOrderQueries: () => {
    return (store.state.orderQueries?.counters?.outstandingCount || 0) > 0
  },

  hasActionableCreditReturns: () => {
    return (store.state.creditReturns?.counters?.outstandingCount || 0) > 0
  },

  hasActionableSupportMessages: (state) => {
    return state.actionableSupportMessages.total > 0
  }
}

/**
 * Actions
 */
const actions = {
  addNotification({ commit }, notification) {
    commit(mutationTypes.REMOVE_NOTIFICATION)
    if (
      typeof notification?.color === 'string' &&
      (!Object.prototype.hasOwnProperty.call(notification, 'closable') ||
        typeof notification.closable === 'boolean') &&
      (!Object.prototype.hasOwnProperty.call(notification, 'timeout') ||
        typeof notification.timeout === 'number') &&
      (typeof notification?.defaultError === 'boolean' ||
        typeof notification?.text === 'string' ||
        Array.isArray(notification?.arrText))
    ) {
      commit(mutationTypes.ADD_NOTIFICATION, notification)
    } else {
      throw new TypeError(
        'format should be { color: "string", closable: "boolean", timeout: "number", defaultError: "boolean", text: "string", arrText: "array" }'
      )
    }
    return Promise.resolve()
  },

  removeNotification({ commit }) {
    commit(mutationTypes.REMOVE_NOTIFICATION)
  },

  async fetchActionableSupportMessages({ commit }, supplierId) {
    try {
      const conversations = await fetchOrderConversationHistory(supplierId)
      const total = conversations.reduce(
        (count, conversation) => count + conversation.unreadCount,
        0
      )
      const orders = conversations.filter(
        (conversation) => conversation.unreadCount
      ).length
      commit(mutationTypes.SET_ACTIONABLE_SUPPORT_MESSAGES, { orders, total })
    } catch (e) {
      commit(mutationTypes.SET_ACTIONABLE_SUPPORT_MESSAGES, {
        orders: 0,
        total: 0
      })
    }
  },

  setAlertsAsSeen({ commit }) {
    commit(mutationTypes.SET_ALERTS_AS_SEEN)
  },

  setNotificationDefaults({ commit }) {
    commit(mutationTypes.SET_NOTIFICATION_DEFAULTS)
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.ADD_NOTIFICATION](state, notification) {
    state.notifications.push({ closable: true, timeout: 5000, ...notification })
  },

  [mutationTypes.REMOVE_NOTIFICATION](state) {
    state.notifications.splice(0)
  },

  [mutationTypes.SET_ALERTS_AS_SEEN](state) {
    state.hasSeenAlerts = true
  },

  [mutationTypes.SET_NOTIFICATION_DEFAULTS](state) {
    Object.assign(state, defaultState)
  },

  [mutationTypes.SET_ACTIONABLE_SUPPORT_MESSAGES](state, messages) {
    state.actionableSupportMessages = messages
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
